/**
 * AssemblyUploadZone — drag-and-drop file upload for VSP3 or STEP/IGES files.
 */

import { useCallback, useRef, useState, type DragEvent } from "react";
import { Box, Text, Flex, Spinner } from "@radix-ui/themes";
import type { Assembly } from "../api/geometry";

const ACCEPTED_EXTENSIONS = [".vsp3", ".stp", ".step", ".igs", ".iges"];
const ACCEPT_STRING = ACCEPTED_EXTENSIONS.join(",");

interface Props {
  onAssemblyCreated: (assembly: Assembly) => void;
  projectId?: string;
}

export function AssemblyUploadZone({ onAssemblyCreated, projectId }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        setError(`Unsupported format: ${ext}. Use ${ACCEPTED_EXTENSIONS.join(", ")}`);
        return;
      }

      setUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("file", file);
        if (projectId) formData.append("project_id", projectId);

        const res = await fetch(
          `${import.meta.env.VITE_API_URL ?? "/api"}/geometry/upload`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!res.ok) {
          const text = await res.text().catch(() => "Upload failed");
          throw new Error(text);
        }

        const assembly: Assembly = await res.json();
        onAssemblyCreated(assembly);
      } catch (e) {
        setError(String(e));
      } finally {
        setUploading(false);
      }
    },
    [onAssemblyCreated, projectId],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <Box>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? "var(--blue-9)" : "var(--gray-7)"}`,
          borderRadius: 8,
          padding: 32,
          textAlign: "center",
          cursor: uploading ? "wait" : "pointer",
          background: dragOver ? "var(--blue-2)" : "var(--gray-2)",
          transition: "all 0.15s",
        }}
      >
        {uploading ? (
          <Flex direction="column" align="center" gap="2">
            <Spinner size="3" />
            <Text size="2" color="gray">Processing geometry...</Text>
          </Flex>
        ) : (
          <Flex direction="column" align="center" gap="2">
            <Text size="4" weight="medium">
              Drop aircraft geometry here
            </Text>
            <Text size="2" color="gray">
              or click to browse
            </Text>
            <Text size="1" color="gray">
              OpenVSP (.vsp3) or CAD (.stp, .step, .igs, .iges)
            </Text>
          </Flex>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_STRING}
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      {error && (
        <Text size="2" color="red" mt="2" style={{ display: "block" }}>
          {error}
        </Text>
      )}
    </Box>
  );
}
